import * as Cookies from "es-cookie";
import gql from "graphql-tag";

// ── The logged-in user's data scope (Branch / Class / Section / Subject) ──────
// An employee is assigned a Branch, one or more Classes and one or more Subjects
// (education_mngt.employee: branch_id / class_ids / subject_ids). A module must
// show that user ONLY the records matching those ids — never every record in the
// institute.
//
// The scope is captured once at login (saveUserScope) and read back by any module
// that lists records (getUserScope). It is deliberately module-agnostic: pass a
// record's branch/class/section/subject id to idInScope() and it tells you whether
// this user may see it.
//
// SECTIONS ARE DERIVED. There is no section_ids column on an employee, so a user's
// sections are the sections of their assigned classes inside their assigned
// branches (Branch → Class → Section). See allowedSectionIds().
//
// NOTE: this is a UI gate. It stops a user from seeing or filtering to records
// outside their assignment, but the records still travel to the browser. The
// resolvers must apply the same scope for it to be an actual access control.

const SCOPE_KEY = "userScope";

// The employee's own record — the authoritative source of their assignment. Used
// when the login payload doesn't carry class_ids, so the scope still resolves
// against real data instead of silently falling back to "sees everything".
const GET_EMPLOYEE_DETAILS = gql`
  query($institute_id: Int, $branch_id: Int) {
    getEmployeeDetails(institute_id: $institute_id, branch_id: $branch_id) {
      employeeId
      branch_id
      class_ids
      subject_ids
    }
  }
`;

// The backend stores these as comma-separated strings ("3,7,9") and is loose about
// the type — a single id can come back as a bare number. Normalise to string ids.
const toIds = (v) =>
  String(v == null ? "" : v)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

// Persist the logged-in employee's assignment ids. Call once at login, with the
// `user` object from the login1 payload.
export function saveUserScope(user) {
  const scope = {
    branchIds: toIds(user && user.branch_id),
    classIds: toIds(user && user.class_ids),
    subjectIds: toIds(user && user.subject_ids),
  };
  try {
    localStorage.setItem(SCOPE_KEY, JSON.stringify(scope));
  } catch (e) {
    /* ignore — a module with no scope falls back to the branch cookie below */
  }
  return scope;
}

// Resolve and persist the logged-in employee's assignment scope. Call this at login
// instead of saveUserScope when an Apollo client is on hand.
//
// The login1 payload is the fast path. When it does NOT carry class_ids — the field
// is absent, as opposed to present-but-empty (a user genuinely assigned no class) —
// fall back to the employee's own record: fetch the employees of their branch and
// match employeeId against the logged-in user id.
export async function resolveUserScope(client, user) {
  const scope = saveUserScope(user);
  const hasClasses = user && user.class_ids !== undefined && user.class_ids !== null;
  if (!client || !user || hasClasses) return scope;

  try {
    const result = await client.query({
      query: GET_EMPLOYEE_DETAILS,
      variables: {
        institute_id: parseInt(user.institution_id) || null,
        // branch_id can come back as a CSV ("1,2"); the query takes a single branch,
        // so look the employee up in their first one.
        branch_id: parseInt(toIds(user.branch_id)[0]) || null,
      },
      fetchPolicy: "network-only",
    });
    const rows = (result.data && result.data.getEmployeeDetails) || [];
    const me = rows.find((r) => String(r.employeeId) === String(user.id));
    if (me) {
      return saveUserScope({
        branch_id: me.branch_id || user.branch_id,
        class_ids: me.class_ids,
        subject_ids: me.subject_ids || user.subject_ids,
      });
    }
    console.warn("[dataScope] no employee row for id", user.id, "— using the login payload.");
  } catch (e) {
    console.warn("[dataScope] getEmployeeDetails failed — using the login payload.", e);
  }
  return scope;
}

// Resolve the logged-in employee's assignment from THEIR OWN employee record, using
// only what's in the cookies (institution id, branch, employee id). Fetches the
// branch's employees and keeps the single row whose employeeId matches the logged-in
// id — no other employee's assignment is ever used.
//
// Call this when a module mounts. Logging in isn't enough on its own: a session that
// was already open has no stored scope, and every filter would be built from an
// empty one. Admins (userlevel 1/2) are unscoped, so this is a no-op for them.
export async function refreshUserScope(client) {
  if (!client || isUnscopedUser()) return getUserScope();

  const instituteId = parseInt(Cookies.get("institutionid")) || null;
  const branchIds = toIds(Cookies.get("branch_id"));
  const myId = Cookies.get("id");
  if (!instituteId || !myId) return getUserScope();

  try {
    const result = await client.query({
      query: GET_EMPLOYEE_DETAILS,
      variables: {
        institute_id: instituteId,
        // branch_id can be a CSV ("1,2"); the query takes one branch, so look the
        // employee up in their first.
        branch_id: parseInt(branchIds[0]) || null,
      },
      fetchPolicy: "network-only",
    });
    const rows = (result.data && result.data.getEmployeeDetails) || [];
    const me = rows.find((r) => String(r.employeeId) === String(myId));
    if (me) {
      return saveUserScope({
        branch_id: me.branch_id || Cookies.get("branch_id"),
        class_ids: me.class_ids,
        subject_ids: me.subject_ids,
      });
    }
    console.warn("[dataScope] no employee row for id", myId, "— keeping the stored scope.");
  } catch (e) {
    console.warn("[dataScope] getEmployeeDetails failed — keeping the stored scope.", e);
  }
  return getUserScope();
}

export function clearUserScope() {
  try {
    localStorage.removeItem(SCOPE_KEY);
  } catch (e) {
    /* ignore */
  }
}

export function getUserScope() {
  try {
    const raw = localStorage.getItem(SCOPE_KEY);
    if (raw && raw !== "" && raw !== "undefined") {
      const s = JSON.parse(raw) || {};
      return {
        branchIds: s.branchIds || [],
        classIds: s.classIds || [],
        subjectIds: s.subjectIds || [],
      };
    }
  } catch (e) {
    /* ignore */
  }
  // A session that predates this (already logged in when it shipped) has no stored
  // scope. Fall back to the branch cookie so the user stays scoped to their branch
  // rather than silently regaining access to the whole institute.
  return { branchIds: toIds(Cookies.get("branch_id")), classIds: [], subjectIds: [] };
}

// Institute admin / management (userlevel 1 & 2) own every branch, so they are not
// scoped. Same rule the module permissions use (see utils/modulePermissions.js).
export function isUnscopedUser() {
  const ul = String(Cookies.get("userlevel") || "");
  return ul === "1" || ul === "2";
}

// Is `id` inside the assigned `allowed` ids?
//   - allowed is empty → that dimension was never assigned → unrestricted. (Don't
//     lock a user out of a dimension the admin never configured.)
//   - allowed is set   → the record must carry a matching id. A record with NO id
//     on a restricted dimension is OUT of scope, so an unattributed record can't
//     leak to everyone.
export function idInScope(allowed, id) {
  if (!allowed || allowed.length === 0) return true;
  if (id == null || id === "") return false;
  return allowed.map(String).includes(String(id));
}

// The section ids this user may see, derived Branch → Class → Section from the
// institute's branch list (getBranches, whose `sections` carry class_id).
// Returns null — meaning "don't filter on section" — when the user is unrestricted
// on both branch and class, or when the branch list hasn't loaded yet.
export function allowedSectionIds(branches, scope = getUserScope()) {
  const { branchIds, classIds } = scope;
  if (branchIds.length === 0 && classIds.length === 0) return null;
  if (!branches || branches.length === 0) return null;
  return branches
    .filter((b) => idInScope(branchIds, b.id))
    .flatMap((b) => b.sections || [])
    .filter((s) => idInScope(classIds, s.class_id))
    .map((s) => String(s.id));
}
